/**
 * BackgroundTaskContext
 * Global context for managing background tasks (e.g., meal plan generation)
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { mealPlanWebSocket, WebSocketMessage } from "../services/websocketClient";
import { useAuth } from "./AuthContext";
import { useToast } from "./ToastContext";
import { useNavigate } from "react-router-dom";
import { taskWorkerApi, profileApi } from "../services/api";

export interface BackgroundTask {
    id: string;
    type:
        | "meal-plan-generation"
        | "meal-plan-preview"
        | "meal-plan-regeneration"
        | "ingredient-detection"
        | "vision-extraction"
        | "ingredient-prediction";
    status: "pending" | "running" | "completed" | "failed";
    message: string;
    createdAt: Date;
    completedAt?: Date;
    result?: any;
    error?: string;
    viewed?: boolean;
    actionUrl?: string;
    tokenUsage?: number; // Number of tokens used for this task
    taskWorkerId?: string; // ID of the task worker event for polling
}

interface BackgroundTaskContextType {
    tasks: BackgroundTask[];
    addTask: (task: Omit<BackgroundTask, "id" | "createdAt">) => string;
    updateTask: (id: string, updates: Partial<BackgroundTask>) => void;
    removeTask: (id: string) => void;
    clearTask: (id: string) => void;
    markAsViewed: (id: string) => void;
    getActiveTasksCount: () => number;
    getUnviewedTasksCount: () => number;
    clearCompletedTasks: () => void;
    navigateToResult: (
        taskId: string,
        navigate: (path: string, options?: any) => void
    ) => void;
    isWebSocketConnected: boolean;
}

const BackgroundTaskContext = createContext<
    BackgroundTaskContextType | undefined
>(undefined);

export const useBackgroundTasks = () => {
    const context = useContext(BackgroundTaskContext);
    if (!context) {
        throw new Error(
            "useBackgroundTasks must be used within a BackgroundTaskProvider"
        );
    }
    return context;
};

interface BackgroundTaskProviderProps {
    children: React.ReactNode;
}

export const BackgroundTaskProvider: React.FC<BackgroundTaskProviderProps> = ({
    children,
}) => {
    const [tasks, setTasks] = useState<BackgroundTask[]>([]);
    const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);
    const { dbUser } = useAuth();
    const { showToast } = useToast();
    const navigate = useNavigate();
    const isSyncingRef = useRef(false);

    // Function to sync pending tasks from the backend
    const syncPendingTasks = useCallback(async () => {
        if (!dbUser) return;

        // Prevent multiple simultaneous syncs
        if (isSyncingRef.current) {
            console.log("[BackgroundTasks] Sync already in progress, skipping");
            return;
        }

        isSyncingRef.current = true;

        try {
            // Fetch pending and submitted tasks from backend
            const pendingEvents = await taskWorkerApi.getUserTaskWorkerEvents({ 
                status: "pending" 
            });
            const submittedEvents = await taskWorkerApi.getUserTaskWorkerEvents({ 
                status: "submitted" 
            });
            
            const allPendingEvents = [...pendingEvents, ...submittedEvents];
            
            console.log(`[BackgroundTasks] Found ${allPendingEvents.length} pending/submitted tasks for user ${dbUser?.id}`);
            
            // TODO: Remove - Debug pending tasks sync
            console.log(`[BackgroundTasks] Found ${allPendingEvents.length} pending tasks to sync`);

            // Add any pending tasks that aren't already being tracked
            setTasks((currentTasks) => {
                const newTasks: BackgroundTask[] = [];
                
                allPendingEvents.forEach((event) => {
                    // Check if we're already tracking this task by taskWorkerId
                    const existingTask = currentTasks.find(
                        (task) => task.taskWorkerId === event.id
                    );

                    if (!existingTask) {
                        // Extract task type from the event
                        const requestUrl = event.input_data?.url || "";
                        const isPreview = requestUrl.includes("/generateMealPlanPreview");
                        const taskType = isPreview ? "meal-plan-preview" : "meal-plan-generation";
                        
                        // Extract profile name if available
                        let profileName = "your profile";
                        if (event.profile_id) {
                            // For now, just use the profile ID since we don't have the profile name in the event
                            // In the future, we could fetch the profile name from the API if needed
                            profileName = `profile ${event.profile_id}`;
                        }

                        // Create a new task
                        const newTask: BackgroundTask = {
                            id: `task-${Date.now()}-${Math.random()}`,
                            type: taskType,
                            status: "running",
                            message: isPreview 
                                ? `Generating meal plan preview for ${profileName}`
                                : `Generating meal plan for ${profileName}`,
                            createdAt: new Date(event.created_at),
                            taskWorkerId: event.id,
                            actionUrl: "/meal-planner/history", // Will be updated when completed
                        };

                        // TODO: Remove - Debug adding synced task
                        console.log(`[BackgroundTasks] Adding synced task:`, newTask);

                        newTasks.push(newTask);
                    }
                });
                
                // Fetch profile names for new tasks if we have any with profile IDs
                if (newTasks.length > 0 && dbUser) {
                    // Get unique profile IDs that need names
                    const profileIdsToFetch = new Set<number>();
                    allPendingEvents.forEach((event) => {
                        if (event.profile_id && newTasks.some(task => task.taskWorkerId === event.id)) {
                            profileIdsToFetch.add(event.profile_id);
                        }
                    });

                    // Fetch profile names asynchronously
                    if (profileIdsToFetch.size > 0) {
                        profileApi.getUserProfiles(dbUser.id).then((response) => {
                            const profileMap = new Map(response.items.map(p => [p.id, p.name]));
                            
                            // Update tasks with actual profile names
                            setTasks((currentTasks) => {
                                return currentTasks.map((task) => {
                                    // Find the corresponding event
                                    const event = allPendingEvents.find(e => e.id === task.taskWorkerId);
                                    if (event?.profile_id && profileMap.has(event.profile_id)) {
                                        const profileName = profileMap.get(event.profile_id);
                                        const isPreview = task.type === "meal-plan-preview";
                                        return {
                                            ...task,
                                            message: isPreview 
                                                ? `Generating meal plan preview for ${profileName}`
                                                : `Generating meal plan for ${profileName}`,
                                        };
                                    }
                                    return task;
                                });
                            });
                        }).catch((error) => {
                            console.error("[BackgroundTasks] Error fetching profile names:", error);
                        });
                    }
                }
                
                // Only show toast if we actually found new tasks
                if (newTasks.length > 0) {
                    // Show a single toast for multiple tasks
                    const message = newTasks.length === 1 
                        ? `Found 1 pending task`
                        : `Found ${newTasks.length} pending tasks`;
                    
                    showToast({
                        message,
                        type: "info",
                        duration: 5000,
                    });
                }
                
                // Return updated tasks array
                return [...currentTasks, ...newTasks];
            });
        } catch (error) {
            console.error("[BackgroundTasks] Error syncing pending tasks:", error);
        } finally {
            isSyncingRef.current = false;
        }
    }, [dbUser, showToast]);

    // Set up WebSocket connection when user is authenticated
    useEffect(() => {
        if (dbUser) {
            // TODO: Remove - Debug WebSocket setup
            console.log("[BackgroundTasks] Setting up WebSocket for user:", dbUser.id);
            
            // Check if WebSocket is already connected to avoid reconnecting
            if (!mealPlanWebSocket.isConnected()) {
                // Connect to WebSocket
                mealPlanWebSocket.connect().catch((error) => {
                    // TODO: Remove - Debug connection error
                    console.error("[BackgroundTasks] Failed to connect WebSocket:", error);
                });
            }

            // Set up message handler
            mealPlanWebSocket.onMessage((message: WebSocketMessage) => {
                handleWebSocketMessage(message);
            });

            // Set up connection state handler
            mealPlanWebSocket.onConnectionStateChange((connected: boolean) => {
                setIsWebSocketConnected(connected);
                // TODO: Remove - Debug connection state
                console.log("[BackgroundTasks] WebSocket connection state:", connected);
            });

            // Sync pending tasks on login with a small delay to prevent rapid multiple calls
            const initialSyncTimeout = setTimeout(() => {
                syncPendingTasks();
            }, 1000);

            // Set up periodic sync every 30 seconds
            const syncInterval = setInterval(() => {
                syncPendingTasks();
            }, 30000);

            // Cleanup on unmount or user change
            return () => {
                // TODO: Remove - Debug cleanup
                console.log("[BackgroundTasks] Cleaning up WebSocket connection");
                mealPlanWebSocket.disconnect();
                clearTimeout(initialSyncTimeout);
                clearInterval(syncInterval);
            };
        } else {
            // TODO: Remove - Debug no user
            console.log("[BackgroundTasks] No user - skipping WebSocket setup");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dbUser?.id]); // Use dbUser.id to prevent re-runs when dbUser object changes but ID is same

    const handleWebSocketMessage = useCallback((message: WebSocketMessage) => {
        // TODO: Remove - Debug message handling
        console.log("[BackgroundTasks] Handling WebSocket message:", message);

        // Validate message has required fields
        if (!message.type || !message.task_id) {
            console.warn("[BackgroundTasks] Invalid WebSocket message - missing type or task_id");
            return;
        }

        // Additional validation for user_id if present
        if (message.user_id && dbUser?.id && message.user_id !== dbUser.id) {
            console.warn(`[BackgroundTasks] Received message for wrong user: ${message.user_id} != ${dbUser.id}`);
            return;
        }

        if (message.type === "meal_plan_completed" && message.task_id) {
            // Find the task by taskWorkerId
            setTasks((prevTasks) => {
                const taskIndex = prevTasks.findIndex(
                    (task) => task.taskWorkerId === message.task_id
                );

                // TODO: Remove - Debug task search
                console.log(`[BackgroundTasks] Looking for task with workerId ${message.task_id} - Found at index: ${taskIndex}`);

                if (taskIndex !== -1) {
                    const updatedTasks = [...prevTasks];
                    const task = updatedTasks[taskIndex];

                    // TODO: Remove - Debug task update
                    console.log("[BackgroundTasks] Updating task:", task.id, "from status:", task.status, "to completed");

                    // Update the task
                    updatedTasks[taskIndex] = {
                        ...task,
                        status: "completed",
                        completedAt: new Date(),
                        message: "Meal plan generated successfully!",
                        actionUrl: message.meal_plan_id
                            ? `/meal-planner/editor?id=${message.meal_plan_id}`
                            : "/meal-planner/history",
                        result: {
                            meal_plan_id: message.meal_plan_id,
                            meal_plan_name: message.meal_plan_name,
                        },
                        viewed: false,
                    };

                    // TODO: Remove - Debug toast notification
                    console.log("[BackgroundTasks] Showing success toast for meal plan:", message.meal_plan_name);

                    // Show toast notification with action
                    showToast({
                        message: `${message.meal_plan_name || "Meal plan"} is ready!`,
                        type: "success",
                        duration: 10000,
                        action: message.meal_plan_id
                            ? {
                                  label: "View Plan",
                                  onClick: () => {
                                      // TODO: Remove - Debug navigation
                                      console.log("[BackgroundTasks] Navigating to meal plan:", message.meal_plan_id);
                                      navigate(`/meal-planner/editor?id=${message.meal_plan_id}`);
                                      // Mark as viewed
                                      updatedTasks[taskIndex].viewed = true;
                                  },
                              }
                            : undefined,
                    });

                    return updatedTasks;
                } else {
                    // TODO: Remove - Debug task not found
                    console.warn("[BackgroundTasks] No task found with workerId:", message.task_id);
                }

                return prevTasks;
            });
        } else if (message.type === "meal_plan_failed" && message.task_id) {
            // Handle failed meal plan tasks
            setTasks((prevTasks) => {
                const taskIndex = prevTasks.findIndex(
                    (task) => task.taskWorkerId === message.task_id
                );

                console.log(`[BackgroundTasks] Looking for failed task with workerId ${message.task_id} - Found at index: ${taskIndex}`);

                if (taskIndex !== -1) {
                    const updatedTasks = [...prevTasks];
                    updatedTasks[taskIndex] = {
                        ...updatedTasks[taskIndex],
                        status: "failed",
                        completedAt: new Date(),
                        error: message.error || "Meal plan generation failed",
                    };

                    // Show error toast
                    const retryInfo = message.retry_count && message.retry_count > 0 
                        ? ` (attempted ${message.retry_count} ${message.retry_count === 1 ? 'retry' : 'retries'})` 
                        : '';
                    
                    showToast({
                        message: `${message.error || "Meal plan generation failed"}${retryInfo}`,
                        type: "error",
                        duration: 10000,
                    });

                    return updatedTasks;
                } else {
                    console.warn("[BackgroundTasks] No task found with workerId:", message.task_id);
                }

                return prevTasks;
            });
        } else {
            // TODO: Remove - Debug unhandled message
            console.log("[BackgroundTasks] Unhandled message type:", message.type);
        }
    }, [showToast, navigate, dbUser]);

    const addTask = useCallback(
        (task: Omit<BackgroundTask, "id" | "createdAt">) => {
            const id = `task-${Date.now()}-${Math.random()}`;
            const newTask: BackgroundTask = {
                ...task,
                id,
                createdAt: new Date(),
            };
            setTasks((prev) => [...prev, newTask]);
            return id;
        },
        []
    );

    const updateTask = useCallback(
        (id: string, updates: Partial<BackgroundTask>) => {
            setTasks((prev) =>
                prev.map((task) =>
                    task.id === id ? { ...task, ...updates } : task
                )
            );
        },
        []
    );

    const removeTask = useCallback((id: string) => {
        setTasks((prev) => prev.filter((task) => task.id !== id));
    }, []);

    const clearTask = useCallback((id: string) => {
        setTasks((prev) => prev.filter((task) => task.id !== id));
    }, []);

    const markAsViewed = useCallback((id: string) => {
        setTasks((prev) =>
            prev.map((task) =>
                task.id === id ? { ...task, viewed: true } : task
            )
        );
    }, []);

    const getActiveTasksCount = useCallback(() => {
        return tasks.filter(
            (task) => task.status === "pending" || task.status === "running"
        ).length;
    }, [tasks]);

    const getUnviewedTasksCount = useCallback(() => {
        return tasks.filter(
            (task) =>
                (task.status === "completed" || task.status === "failed") &&
                !task.viewed
        ).length;
    }, [tasks]);

    const clearCompletedTasks = useCallback(() => {
        setTasks((prev) =>
            prev.filter(
                (task) => task.status === "pending" || task.status === "running"
            )
        );
    }, []);

    const navigateToResult = useCallback(
        (taskId: string, navigate: (path: string, options?: any) => void) => {
            const task = tasks.find((t) => t.id === taskId);
            if (task?.status === "completed" && task.result) {
                markAsViewed(taskId);
                // Navigate to editor with meal plan ID
                // Check both possible property names for backward compatibility
                const mealPlanId = task.result.meal_plan_id || task.result.id;
                if (mealPlanId) {
                    navigate(`/meal-planner/editor?id=${mealPlanId}`);
                } else {
                    // Fallback to history page if no ID found
                    navigate("/meal-planner/history");
                }
            }
        },
        [tasks, markAsViewed]
    );

    return (
        <BackgroundTaskContext.Provider
            value={{
                tasks,
                addTask,
                updateTask,
                removeTask,
                clearTask,
                markAsViewed,
                getActiveTasksCount,
                getUnviewedTasksCount,
                clearCompletedTasks,
                navigateToResult,
                isWebSocketConnected,
            }}
        >
            {children}
        </BackgroundTaskContext.Provider>
    );
};
